import React, { useEffect, useRef } from "react";
import { PayoutWrapper, Heading, Paragraph, InputBox } from "../../styles";
import Local from "./Transfers/Local";
import International from "./Transfers/International";
import { motion } from "framer-motion";
import { nextVariants } from "../../../../Wallets/utils";
import CustomSelect from "../../../../../components/common/CustomSelect";
import { useForm, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { clearSettlementDetails, setSettlementDetails } from "../../SettlementPayoutSlice";
import { LocalVAPayoutMethods, VAPayoutMethods } from "../../../../Settlements/utils";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FEATURE_FLAG_KEYS } from "../../../../../utils/featureFlags";
import { useParams } from "react-router-dom";

interface Props {
  handleNext: () => void;
  activeCurrency: string;
  setActiveCurrency: (data: string) => void;
  amount: number;
  setAmount: any;
  beneficiaryAmount: number;
  setBeneficiaryAmount: (data: number) => void;
  internationalCurrency: any;
  setInternationalCurrency: any;
  intRate?: number;
  setIntRate?: (data: number) => void;
  intTCharges: number;
  setIntTcharges?: (data: number) => void;
  intAmount?: number;
  setIntAmount?: (data: number) => void;
  interNationalFee?: number;
  setInternationalFee?: any;
  country: string;
  setCountry: (data: string) => void;
  sendingCountriesData: string[];
  setSendingCountriesData: (data: string[]) => void;
}

type PayoutType = {
  payoutMethod: string;
};

const PayOut = ({
  handleNext,
  activeCurrency,
  setActiveCurrency,
  amount,
  setAmount,
  internationalCurrency,
  setInternationalCurrency,
  intRate,
  setIntRate,
  intTCharges,
  setIntTcharges,
  intAmount,
  setIntAmount,
  interNationalFee,
  setInternationalFee,
  country,
  setCountry,
  beneficiaryAmount,
  setBeneficiaryAmount,
  sendingCountriesData,
  setSendingCountriesData,
}: Props) => {
  const dispatch = useAppDispatch();
  const { currencyCode } = useParams();
  const capitalizedCurrencyCode = currencyCode?.toUpperCase() || "";

  const { watch, control, setValue } = useForm<PayoutType>();
  const selectedPayoutMethod = watch("payoutMethod")

  const getSavedSettlementDetails = useAppSelector((state) => state?.settlementPayout?.settlementDetails);

  const handlePayoutMethodChange = (method) => {
    dispatch(clearSettlementDetails())
    dispatch(setSettlementDetails({ payoutMethod: method }));
  };

  useEffect(() => {
    if (getSavedSettlementDetails) {
      Object.keys(getSavedSettlementDetails)?.forEach((key) => {
        setValue(
          key as keyof PayoutType,
          getSavedSettlementDetails[key as keyof PayoutType],
        );
      });
    }
  }, [getSavedSettlementDetails, setValue]);
  
  const enableVirtualAccountsV2 = useFeatureFlagEnabled(FEATURE_FLAG_KEYS.virtualAccountsV2);
  const flaggedAvailableMethods = enableVirtualAccountsV2 ? 
    VAPayoutMethods.filter(method => method.currency === capitalizedCurrencyCode || method.currency === "USD") : 
    VAPayoutMethods.filter(method => method.value !== 'ghsPayout');

  return (
    <PayoutWrapper>
      <Heading>Payout request</Heading>
      <Paragraph>
        Payouts typically take 1-3 working days.
      </Paragraph>

      <InputBox>
        <Controller
          name="payoutMethod"
          control={control}
          rules={{ required: "Select a payout method." }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              lightBorder
              placeholder="Select payment method"
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                handlePayoutMethodChange(value);
              }}
              options={flaggedAvailableMethods}
            />
          )}
        />
      </InputBox>



      {LocalVAPayoutMethods.includes(selectedPayoutMethod) && (
        <React.Fragment>
          <motion.div
            variants={nextVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            key="local"
          >
            <Local
              onNext={handleNext}
              setInputtedAmount={setAmount}
              inputtedAmount={amount}
              beneFiciaryAmount={beneficiaryAmount}
              setBeneficiaryAmount={setBeneficiaryAmount}
            />
          </motion.div>
        </React.Fragment>
      )}

      {selectedPayoutMethod === "intPayout" && (
        <React.Fragment>
          <motion.div
            variants={nextVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            key="international"
          >
            <International
              onNext={handleNext}
              selectedSendingCurrency={internationalCurrency}
              setSelectedSendingCurrency={setInternationalCurrency}
              rate={intRate}
              setRate={setIntRate}
              setTotalCharges={setIntTcharges}
              totalCharges={intTCharges}
              intAmount={intAmount}
              setIntAmount={setIntAmount}
              internationalFee={interNationalFee}
              setInternationalFee={setInternationalFee}
              selectedCountry={country}
              setSelectedCountry={setCountry}
              sendingCountriesData={sendingCountriesData}
              setSendingCountriesData={setSendingCountriesData}
            />
          </motion.div>
        </React.Fragment>
      )}
    </PayoutWrapper>
  );
};

export default PayOut;
